import { useSelector } from 'react-redux';
import { PLAN_MAP } from 'src/sections/pricing/pricingUtils';

/**
 * Hook to check a single plan feature.
 *
 * @param {string} featureCode - e.g. 'COPY_PAST_JD', 'MAX_PROJECTS'
 * @returns {{
 *   enabled: boolean,
 *   value: any,
 *   type: string,
 *   label: string,
 *   isUnlocked: boolean,
 *   isLocked: boolean,
 *   requiredPlan: string,
 *   shouldShowBadge: boolean
 * }}
 *
 * For BOOLEAN features: enabled = isUnlocked && feature.value === true
 * For NUMERIC features: enabled = isUnlocked, value = the limit
 * For ENUM features: enabled = isUnlocked, value = the enum string
 * If feature not found: enabled = true (fail-open so existing functionality is unaffected)
 */
export function usePlanFeature(featureCode) {
  const planState = useSelector((state) => state.plan || {});
  const { features = {}, currentPlanTier = '', isTrial = false } = planState;

  const feature = features[featureCode];

  if (!feature) {
    // Feature not in plan config — fail-open (don't block anything)
    return {
      enabled: true,
      value: null,
      type: null,
      label: null,
      isUnlocked: true,
      isLocked: false,
      requiredPlan: null,
      shouldShowBadge: false,
    };
  }

  // Get current tenant tier ranking
  const currentPlanTierLower = (currentPlanTier || 'FREE').toLowerCase();
  const currentTierRank = PLAN_MAP[currentPlanTierLower] ?? 0;

  const requiredPlan = feature.requiredPlan || 'FREE';
  const requiredPlanLower = requiredPlan.toLowerCase();
  const requiredPlanRank = PLAN_MAP[requiredPlanLower] ?? 0;

  // Determine if unlocked:
  // 1. If backend explicitly provides isUnlocked, use it.
  // 2. Fallback: if it's trial, Premium features are unlocked.
  // 3. Fallback: currentTierRank >= requiredPlanRank
  let isUnlocked = true;
  if (typeof feature.isUnlocked === 'boolean') {
    isUnlocked = feature.isUnlocked;
  } else if (isTrial) {
    // Trial allows Standard and Premium, locks Enterprise
    isUnlocked = requiredPlan.toUpperCase() !== 'ENTERPRISE';
  } else {
    isUnlocked = currentTierRank >= requiredPlanRank;
  }

  // Determine enabled:
  // For BOOLEAN features: isUnlocked && value === true
  // For other features: isUnlocked
  let enabled = isUnlocked;
  if (feature.type === 'BOOLEAN' && isUnlocked) {
    enabled = feature.value === true;
  }

  // Determine if we should show the badge/label/icon:
  // - Enterprise plan users never see any badges.
  // - Other plans show badges for features that require a higher tier than the user's base plan.
  // - Trial users show badges for Standard, Premium, and Enterprise features (even if Standard/Premium are unlocked).
  const isEnterpriseUser = currentPlanTier.toUpperCase() === 'ENTERPRISE';
  let shouldShowBadge = false;

  if (!isEnterpriseUser) {
    if (isTrial) {
      // Trial user: show badges for Standard, Premium, Enterprise features
      shouldShowBadge = requiredPlanRank > 0; // standard (1), premium (2), enterprise (3)
    } else {
      // Non-trial user: show badges for features higher than their current tier
      shouldShowBadge = requiredPlanRank > currentTierRank;
    }
  }

  return {
    enabled,
    value: feature.value,
    type: feature.type,
    label: feature.label,
    isUnlocked,
    isLocked: !isUnlocked,
    requiredPlan,
    shouldShowBadge,
  };
}

/**
 * Hook to get the entire plan state from Redux.
 * Useful when you need to check multiple features at once.
 */
export function usePlanFeatures() {
  return useSelector((state) => state.plan || {});
}
